import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { api } from "../../config";

const API_URL = `${api.API_URL}/portal`;

export const validatePortalLink = createAsyncThunk(
  "portal/validateLink",
  async (token, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/${token}/validate`);
      // api_helper interceptor already unwraps response.data, so response IS the data
      console.log("Validate response:", response);
      return response;
    } catch (error) {
      console.error("Portal validation error:", error);
      return rejectWithValue(error.response?.data || error.response || { error: "Validation failed" });
    }
  }
);

export const getProviderInfo = createAsyncThunk(
  "portal/getProviderInfo",
  async (token, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/${token}/provider-info`);
      console.log("Provider info response:", response);
      return response;
    } catch (error) {
      console.error("Provider info error:", error);
      return rejectWithValue(error.response?.data || error.response || { error: "Failed to load provider info" });
    }
  }
);

export const getCustomerInfo = createAsyncThunk(
  "portal/getCustomerInfo",
  async (token, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/${token}/customer-info`);
      console.log("Customer info response:", response);
      return response;
    } catch (error) {
      console.error("Customer info error:", error);
      return rejectWithValue(error.response?.data || error.response || { error: "Failed to load customer info" });
    }
  }
);

export const getReminders = createAsyncThunk(
  "portal/getReminders",
  async (token, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/${token}/reminders`);
      console.log("Reminders response:", response);
      return response;
    } catch (error) {
      console.error("Reminders error:", error);
      return rejectWithValue(error.response?.data || error.response || { error: "Failed to load reminders" });
    }
  }
);

export const getUpcomingReminders = createAsyncThunk(
  "portal/getUpcomingReminders",
  async (token, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/${token}/upcoming-reminders`);
      console.log("Upcoming reminders response:", response);
      return response;
    } catch (error) {
      console.error("Upcoming reminders error:", error);
      return rejectWithValue(error.response?.data || error.response || { error: "Failed to load upcoming reminders" });
    }
  }
);

export const submitServiceRequest = createAsyncThunk(
  "portal/submitServiceRequest",
  async ({ token, data }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_URL}/${token}/request-service`, data);
      return response;
    } catch (error) {
      console.error("Service request error:", error);
      return rejectWithValue(error.response?.data || error.response || { error: "Failed to submit service request" });
    }
  }
);

export const submitCallRequest = createAsyncThunk(
  "portal/submitCallRequest",
  async ({ token, data }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_URL}/${token}/request-call`, data);
      return response;
    } catch (error) {
      console.error("Call request error:", error);
      return rejectWithValue(error.response?.data || error.response || { error: "Failed to submit call request" });
    }
  }
);

export const sendMessage = createAsyncThunk(
  "portal/sendMessage",
  async ({ token, data }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_URL}/${token}/send-message`, data);
      return response;
    } catch (error) {
      console.error("Send message error:", error);
      return rejectWithValue(error.response?.data || error.response || { error: "Failed to send message" });
    }
  }
);

export const confirmReminder = createAsyncThunk(
  "portal/confirmReminder",
  async ({ token, reminderId }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_URL}/${token}/confirm-reminder/${reminderId}`);
      return response;
    } catch (error) {
      console.error("Confirm reminder error:", error);
      return rejectWithValue(error.response?.data || error.response || { error: "Failed to confirm reminder" });
    }
  }
);

export const rescheduleReminder = createAsyncThunk(
  "portal/rescheduleReminder",
  async ({ token, reminderId, data }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_URL}/${token}/reschedule-reminder/${reminderId}`, data);
      return response;
    } catch (error) {
      console.error("Reschedule reminder error:", error);
      return rejectWithValue(error.response?.data || error.response || { error: "Failed to reschedule reminder" });
    }
  }
);

export const getServiceHistory = createAsyncThunk(
  "portal/getServiceHistory",
  async (token, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/${token}/service-history`);
      console.log("Service history response:", response);
      console.log("Service history data:", response?.data);
      console.log("Service history services:", response?.data?.services);
      return response;
    } catch (error) {
      console.error("Service history error:", error);
      return rejectWithValue(error.response?.data || error.response || { error: "Failed to load service history" });
    }
  }
);

export const getCustomerRequests = createAsyncThunk(
  "portal/getCustomerRequests",
  async (token, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/${token}/customer-requests`);
      console.log("Customer requests response:", response);
      console.log("Customer requests data:", response?.data);
      return response;
    } catch (error) {
      console.error("Customer requests error:", error);
      return rejectWithValue(error.response?.data || error.response || { error: "Failed to load customer requests" });
    }
  }
);

const initialState = {
  token: null,
  isValid: false,
  customMessage: "",
  providerInfo: null,
  customerInfo: null,
  reminders: [],
  upcomingReminders: [],
  serviceHistory: [],
  customerRequests: {
    serviceRequests: [],
    callRequests: [],
    messages: [],
    totalRequests: 0
  },
  loading: false,
  error: null,
  submissionSuccess: false,
};

const portalSlice = createSlice({
  name: "portal",
  initialState,
  reducers: {
    setToken: (state, action) => {
      state.token = action.payload;
    },
    resetPortalState: (state) => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(validatePortalLink.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(validatePortalLink.fulfilled, (state, action) => {
        console.log("Portal validation fulfilled, payload:", action.payload);
        state.loading = false;
        state.isValid = action.payload?.valid === true;  // Strict true check
        state.customMessage = action.payload?.customMessage || "";
        if (!state.isValid) {
          state.error = "Portal link validation failed";
        }
      })
      .addCase(validatePortalLink.rejected, (state, action) => {
        console.log("Portal validation rejected:", action.payload);
        state.loading = false;
        state.isValid = false;
        state.error = action.payload?.error || "Invalid or expired portal link";
      })
      .addCase(getProviderInfo.pending, (state) => {
        state.loading = true;
      })
      .addCase(getProviderInfo.fulfilled, (state, action) => {
        state.loading = false;
        state.providerInfo = action.payload || null;
      })
      .addCase(getProviderInfo.rejected, (state) => {
        state.loading = false;
        state.error = "Failed to load provider info";
      })
      .addCase(getCustomerInfo.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCustomerInfo.fulfilled, (state, action) => {
        state.loading = false;
        state.customerInfo = action.payload || null;
      })
      .addCase(getCustomerInfo.rejected, (state) => {
        state.loading = false;
        state.error = "Failed to load customer info";
      })
      .addCase(getReminders.fulfilled, (state, action) => {
        state.reminders = action.payload?.data || [];
      })
      .addCase(getReminders.rejected, (state) => {
        state.reminders = [];
      })
      .addCase(getUpcomingReminders.fulfilled, (state, action) => {
        state.upcomingReminders = action.payload?.data || [];
      })
      .addCase(getUpcomingReminders.rejected, (state) => {
        state.upcomingReminders = [];
      })
      .addCase(submitServiceRequest.pending, (state) => {
        state.loading = true;
        state.submissionSuccess = false;
      })
      .addCase(submitServiceRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.submissionSuccess = true;
        state.error = null;
      })
      .addCase(submitServiceRequest.rejected, (state) => {
        state.loading = false;
        state.submissionSuccess = false;
        state.error = "Failed to submit service request";
      })
      .addCase(submitCallRequest.fulfilled, (state) => {
        state.submissionSuccess = true;
        state.error = null;
      })
      .addCase(submitCallRequest.rejected, (state) => {
        state.submissionSuccess = false;
        state.error = "Failed to submit call request";
      })
      .addCase(sendMessage.fulfilled, (state) => {
        state.submissionSuccess = true;
        state.error = null;
      })
      .addCase(sendMessage.rejected, (state) => {
        state.submissionSuccess = false;
        state.error = "Failed to send message";
      })
      .addCase(confirmReminder.fulfilled, (state) => {
        state.submissionSuccess = true;
        state.error = null;
      })
      .addCase(confirmReminder.rejected, (state) => {
        state.submissionSuccess = false;
        state.error = "Failed to confirm reminder";
      })
      .addCase(rescheduleReminder.fulfilled, (state) => {
        state.submissionSuccess = true;
        state.error = null;
      })
      .addCase(rescheduleReminder.rejected, (state) => {
        state.submissionSuccess = false;
        state.error = "Failed to reschedule reminder";
      })
      .addCase(getServiceHistory.pending, (state) => {
        state.loading = true;
      })
      .addCase(getServiceHistory.fulfilled, (state, action) => {
        state.loading = false;
        console.log("Storing service history, action.payload:", action.payload);
        console.log("Services to store:", action.payload?.data?.services);
        state.serviceHistory = action.payload?.data?.services || [];
      })
      .addCase(getServiceHistory.rejected, (state) => {
        state.loading = false;
        state.serviceHistory = [];
      })
      .addCase(getCustomerRequests.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCustomerRequests.fulfilled, (state, action) => {
        state.loading = false;
        console.log("Storing customer requests, action.payload:", action.payload);
        console.log("Data to store:", action.payload?.data);
        state.customerRequests = action.payload?.data || {
          serviceRequests: [],
          callRequests: [],
          messages: [],
          totalRequests: 0
        };
      })
      .addCase(getCustomerRequests.rejected, (state) => {
        state.loading = false;
        state.customerRequests = {
          serviceRequests: [],
          callRequests: [],
          messages: [],
          totalRequests: 0
        };
      });
  },
});

export const { setToken, resetPortalState } = portalSlice.actions;
export default portalSlice.reducer;
